import {FC, useRef, useEffect, useState} from 'react'
import {shallowEqual, useSelector, connect, useDispatch, ConnectedProps} from 'react-redux'
import {LayoutSplashScreen} from '../../../../cms/layout/core'
import * as auth from './actions'
import {RootState} from '../../../../setup'
import {service} from './service'
import {isEmpty} from 'lodash'
import {ISettingTypeState} from '../../siteSettings/components/settings'
import {imageBaseUrl} from 'src/cms/helpers/constants'
import AuthService from 'src/cms/constants/authService'
import AuthBrandedLayout from '../components/AuthBrandedLayout'
import {API_URL} from 'src/cms/constants/EndPoint'
import axios from 'axios'

const mapState = (state: RootState) => ({auth: state.auth})
const connector = connect(mapState, auth.actions)
type PropsFromRedux = ConnectedProps<typeof connector>

const AuthInit: FC<PropsFromRedux> = (props) => {
  const didRequest = useRef(false)
  const dispatch = useDispatch()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const [profileError, setProfileError] = useState<string | null>(null)
  const {accessToken, guestToken}: any = useSelector<RootState>(({auth}) => auth, shallowEqual)
  const [splashLogo, setSplashLogo] = useState('')
  const [splashLogoAlt, setSplashLogoAlt] = useState('')
  const settingTypeData: ISettingTypeState = useSelector((state: any) => state.settingType)
  const splashScreen = document.getElementById('splash-screen')

  useEffect(() => {
    if (!isEmpty(settingTypeData?.backendData)) {
      settingTypeData?.backendData &&
        settingTypeData?.backendData['Theme settings']?.map((item: any) => {
          if (item?.name === 'loadingImage') {
            setSplashLogo(item?.value)
            setSplashLogoAlt(item?.name)
          }
        })
    }
  }, [settingTypeData])
  useEffect(() => {
    if (!isEmpty(splashLogo)) {
      let img = document.createElement('img')
      img.setAttribute('src', `${imageBaseUrl}/${splashLogo}`)
      img.setAttribute('alt', `${splashLogoAlt}`)
      img.setAttribute('class', 'h-30px')

      splashScreen?.appendChild(img)
    }
  }, [splashLogo])

  // We should request user by authToken before rendering the application
  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const {data: user} = await service.getUserByToken()
          await dispatch(props.fulfillUser(user))
          await dispatch(auth.actions.getUserModule())
        }
      } catch (error: any) {
        if (!didRequest.current) {
          if (error?.response?.status === 401) {
            // Token is actually invalid — let a fresh SSO login fix it
            dispatch(props.logout())
          } else {
            // Any other failure (e.g. 400): show the error instead of logging
            // out, otherwise the /sso redirect auto-logs back in and loops
            const status = error?.response?.status
            let message: string
            if (status >= 500) {
              // Server-side failure. The body is usually useless here — Laravel
              // sends a bare "Server Error" with debug off, nginx sends an HTML
              // 502/504 page — so use our own wording and always surface the
              // code so support can match it against the API logs.
              message = `The server could not return your profile (HTTP ${status}). Please try again in a moment.`
            } else {
              message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                (status
                  ? `Failed to load user profile (status ${status})`
                  : 'Failed to load user profile. Please try again.')
              // The backend's "Please set your password" message is misleading
              // here — passwords are managed by the SSO provider
              if (/set your password/i.test(message)) {
                message = 'Failed to load user profile. Please try again or contact support.'
              }
            }
            setProfileError(message)
          }
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }
    if (accessToken) {
      requestUser()
    } else {
      // if (!guestToken) {
      //   dispatch(props?.getGuestToken())
      // }
      dispatch(props.logout())
      setShowSplashScreen(false)
    }
  }, [])

  const handleLogout = async () => {
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim()
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    })
    // Demo branch: local-only signout (no SSO), then reload into /auth/login.
    await new AuthService().logout()
    window.location.replace('/auth/login')
  }

  if (!showSplashScreen && profileError) {
    // Same branded split-screen shell as /auth/login — the message and the two
    // actions take the place the email/password form normally occupies.
    // AuthInit renders ABOVE ThemeProvider/GlobalStyles in App.tsx, so the
    // theme's `.login-wrapper button` primary-color rule is not live here:
    // colour the buttons explicitly (btn-danger is the brand red, $danger
    // #ed1b2f) instead of relying on it.
    return (
      <AuthBrandedLayout>
        <div className='pt-8 text-center' data-cy='profile-error-screen'>
          <h4 className='login-box-msg text-center mt-10 mb-5' data-cy='profile-error-heading'>
            Unable to load profile
          </h4>

          <div className='fs-6 mb-2'>You are signed in, but your profile could not be loaded.</div>
          <div className='text-danger fw-bolder fs-6 mb-8' data-cy='profile-error-message'>
            {profileError}
          </div>

          <button
            type='button'
            className='btn btn-lg btn-danger text-white w-100 mb-4'
            onClick={() => window.location.reload()}
            data-cy='profile-error-retry-button'
          >
            Retry
          </button>

          <button
            type='button'
            className='btn btn-lg btn-light fw-bold w-100 mb-5'
            onClick={handleLogout}
            data-cy='profile-error-logout-button'
          >
            Logout
          </button>
        </div>
      </AuthBrandedLayout>
    )
  }

  return showSplashScreen ? <LayoutSplashScreen /> : <>{props.children}</>
}

export default connector(AuthInit)
